import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as urlTemplate from 'url-template';
import omit from 'lodash-es/omit';

import { NewAirSearchPayload } from '@flight-results/entities/request-search.payload';
import { NewAirResultsResponse } from '@app/entities/flight-results';
import { FetchFiltersPayload } from '@flight-results/entities/fetch-filters.payload';
import { FetchFlightsOptionPayload } from '@flight-results/entities/fetch-flights-option.payload';
import { flightResultsApi } from '@flight-results/flight-results.api';
import { UpsellResponse } from '@shared/flight-itinerary/entities/upsell-flights';
import { FlexibleDatesPayload } from '@flight-results/entities/flexible-dates.payload';
import { FlexibleDatesResult } from '@flight-results/entities/flexible.dates';
import { ExchangeBookPayload, ExchangeBookResponse } from '@user-history/entities/user-history-item';
import { userHistoryApi } from '@user-history/user-history.api';
import { RappiPayment } from '@booking/entities/rappi-payment';
import { RappiPaymentResponse, RappiIntermediaryPaymentResponse } from '@booking/entities/rappi-payment.response';
import {
  FlightDetailCabinPayload, FlightDetailCabinResponse, FlightResultsCabinPayload, FlightResultsCabinResponse
} from '@shared/flight-itinerary/entities/cabin-types';
import { getUriFlightKeys } from '@flight-results/mappers/flight-keys.mapper';
import { AirFiltersResponse } from '@shared/magic-filter-bar/entities/filter-types';
import { SorterTypes } from '@app/modules/shared/filters/entities/sorter-options';

@Injectable()
export class FlightResultsService {

  constructor(
    private http: HttpClient
  ) { }

  public search(payload: NewAirSearchPayload): Observable<NewAirResultsResponse> {
    if (!payload?.Results?.OrderCriteria) {
      payload = {...payload, Results: {...payload.Results, OrderCriteria: SorterTypes.Recommended }};
    }
    return this.http.post<NewAirResultsResponse>(flightResultsApi.air_search, payload);
  }

  public searchBySector(payload: NewAirSearchPayload): Observable<NewAirResultsResponse> {
    if (!payload?.Results.OrderCriteria) {
      payload = {...payload, Results: {...payload.Results, OrderCriteria: SorterTypes.Recommended }};
    }
    return this.http.post<NewAirResultsResponse>(flightResultsApi.air_search_by_sector, payload);
  }

  public searchFlexDates(payload: FlexibleDatesPayload): Observable<FlexibleDatesResult> {
    const headers = new HttpHeaders({ 'ignoreLoadingBar': '' });
    return this.http.post<FlexibleDatesResult>(
      flightResultsApi.air_flex_dates, payload, { headers });
  }

  public fetchFilters(payload: FetchFiltersPayload): Observable<AirFiltersResponse> {
    const headers = new HttpHeaders({
      'Authorization': payload.SessionToken
    });
    const url = urlTemplate.parse(flightResultsApi.air_filter).expand({
      searchId: payload.SearchId
    });

    return this.http.get<AirFiltersResponse>(url, { headers });
  }

  public fetchFiltersBySector(payload: FetchFiltersPayload): Observable<AirFiltersResponse> {
    const headers = new HttpHeaders({
      'Authorization': payload.SessionToken
    });
    const {SearchId, SessionToken, OptionSelectionIds } = payload
    const url = urlTemplate.parse(flightResultsApi.air_filter_by_sector).expand({
      SearchId, SessionToken, OptionSelectionIds
    });

    return this.http.post<AirFiltersResponse>(url, payload, { headers });
  }

  public fetchFlightsOption(payload: FetchFlightsOptionPayload): Observable<{ OptionId: number }> {
    const headers = new HttpHeaders({
      'ignoreLoadingBar': '',
      'Authorization': payload.SessionToken
    });
    let url = urlTemplate.parse(flightResultsApi.air_option).expand({
      searchId: payload.SearchId
    });
    const flightKeys = getUriFlightKeys(payload?.FlightKeys);
    url = `${url}${flightKeys}`;
    return this.http.get<{ OptionId: number }>(url, { headers });
  }

  public fetchUpsellOptions(optionId: number, sessionToken: string, searchId: string): Observable<UpsellResponse> {
    const headers = new HttpHeaders({
      'ignoreLoadingBar': '',
      'Authorization': sessionToken
    });
    const url = urlTemplate.parse(flightResultsApi.air_upsell).expand({
      optionId: optionId,
      searchId: searchId
    });

    return this.http.get<UpsellResponse>(url, { headers: headers });
  }

  public searchExchange(payload: NewAirSearchPayload): Observable<NewAirResultsResponse> {
    if (!payload?.Results?.OrderCriteria) {
      payload = {...payload, Results: {...payload.Results, OrderCriteria: SorterTypes.Recommended }};
    }
    const headers = new HttpHeaders({
      'Authorization': payload.SessionToken
    });
    const clearPayload = omit(payload, ['Adults', 'Children', 'Infants', 'exchangeDisabledSector']);
    return this.http.post<NewAirResultsResponse>(userHistoryApi.air_search_exchange, clearPayload, { headers });
  }

  public exchangeBook(payload: ExchangeBookPayload): Observable<ExchangeBookResponse> {
    return this.http.post<ExchangeBookResponse>(userHistoryApi.air_exchange_book, {
      ...payload, FirstPaxEmail: ''
    });
  }

  public fetchRappiPayment(payload: RappiPayment): Observable<RappiPaymentResponse> {
    const url = urlTemplate.parse(userHistoryApi.rappi_payment).expand({
      TravelItineraryId: payload.TravelItineraryId
    });
    return this.http.get<RappiPaymentResponse>(url);
  }

  public fetchFlightResultsCabinAmenities(payload: FlightResultsCabinPayload): Observable<FlightResultsCabinResponse> {
    const headers = new HttpHeaders({
      'content-type': 'application/json'
    });
    return this.http.post<FlightResultsCabinResponse>(flightResultsApi.air_cabin_amenities, payload, { headers });
  }

  public fetchFlightDetailCabin(payload: FlightDetailCabinPayload): Observable<FlightDetailCabinResponse> {
    const headers = new HttpHeaders({
      'content-type': 'application/json'
    });
    return this.http.post<FlightDetailCabinResponse>(flightResultsApi.air_cabin_detail, payload, { headers });
  }

  public fetchRappiIntermediaryPaymentChangeDesktopAction(token: string): Observable<RappiIntermediaryPaymentResponse> {
    const payload = { token: token };
    return this.http.post<RappiIntermediaryPaymentResponse>(flightResultsApi.rappi_intermediary_payment, payload);
  }
}
